import * as React from 'react';
import {
  AsyncTypeahead,
  Menu,
  MenuItem,
  UseAsyncProps
} from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.scss';
import './referenceSearch.scss';
import { Metadata, Schema, SchemaField } from '../../types/Metadata';
import { NodeType } from '../../enums/NodeType';
import { cn, getSchema, parseField } from '../../utils';
import { Close, Icon } from 'rcd-cpp-ui/ui/icon';
import { Pill } from '../Pill/pill';
import { API } from '../../api';
import { SearchInput } from '../SearchInput/searchInput';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import InfiniteLoader from '../InfiniteLoader/infiniteLoader';
import { SeeOption } from '../../enums/SeeOption';
import { useSelector } from '../../utils/useSelector';

type Props<D = Metadata, T = NT> = Partial<Omit<UseAsyncProps, 'onChange'>> & {
  selected?: (string | D)[];
  inlineMenu?: boolean;
  api?: Pick<API, 'searchReferences'>;
  onChange?: (values: D[]) => void;
  cache?: { [x: string]: D | any };
  hideIcon?: boolean;
  includeIds?: string[];
  includeInactives?: boolean;
  includeBlanks?: boolean;
  getClassName?: (o: Option) => string;
  shouldMask?: boolean;
  seeOptions?: SeeOption[];
  schemaField?: SchemaField;
  refreshSearchTrigger?: boolean;
  setRefreshSearchTrigger?: any;
  excludeRecords?: Metadata[];
} & T;
type NT = { nodeType: NodeType };
type F = { field: string };
type Option = Metadata & { rsLabel: string };

const _getSchema = (
  schemas: Schema[],
  p: Pick<Props<any, NT | F>, 'includeInactives'>,
  fieldKey?: string
): {
  nodeType: NodeType;
  field: string;
  activeField: string;
  orderField: string;
  fieldKey?: string;
} => {
  let { nodeType, field } = p as any;
  let fields: SchemaField[] = null;
  if (nodeType && !field) {
    const schema = getSchema(schemas, nodeType);
    if (schema?.props) {
      field = schema.props.displayField;
      fields = schema.props.fields;
    }
  } else if (field && !nodeType) {
    const sf = parseField(field, schemas);
    if (sf) {
      nodeType = sf.schemaType;
      field = sf.field;
      fields = sf.schema?.props?.fields;
    }
  }
  const activeField = p.includeInactives
    ? null
    : findOne(fields, { field: 'isActive' });
  const orderField = findOne(fields, { isOrderField: true });
  return {
    nodeType,
    field,
    activeField: activeField?.field,
    orderField: orderField?.field,
    fieldKey
  };
};

export const ReferenceSearch = <D extends Metadata = Metadata, T = NT | F>({
  //nodeType,
  selected,
  inlineMenu,
  multiple,
  api,
  cache = {},
  maxResults,
  minLength,
  onChange,
  hideIcon,
  includeIds,
  includeBlanks,
  seeOptions,
  getClassName = () => null,
  shouldMask,
  schemaField,
  excludeRecords,
  refreshSearchTrigger,
  setRefreshSearchTrigger,
  ...props
}: Props<D, T>) => {
  const schemas = useSelector(s => s.planning?.selectedWorkspace?.schemas);
  const getAPI = useSelector(s => s.app?.elFn?.getAPI);
  const [seeOption, setSeeOpt] = React.useState<SeeOption>(null);
  const [text, setText] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [options, setOptions] = React.useState<Option[]>([]);
  const [values, setValues] = React.useState<Option[]>([]);
  const [prevExcludeRecords, setPrevExcludeRecords] = React.useState<
    Metadata[]
  >([]);
  const { nodeType, field, fieldKey, activeField, orderField } = _getSchema(
    schemas,
    props,
    schemaField?.key
  );

  const typeaheadRef = React.useRef(null);
  const toOptions = (m: Metadata[]) =>
    m?.map((r: Metadata) => ({
      ...r,
      rsLabel: r.props[field] || r.props.name || ''
    })) || [];

  const toData = (arr: any[]): (string | Metadata)[] => {
    if (typeof arr == 'string') {
      if (cache[arr]) return [cache[arr]];
      return [arr];
    }
    return arr?.map(s => {
      if (typeof s == 'string') {
        if (cache[s]) return cache[s];
        return s;
      }
      return s;
    });
  };

  React.useEffect(() => {
    const { searchReferences } = api || getAPI();
    const values = toData(selected);
    const ids = values?.filter(s => typeof s == 'string') as string[];
    if (!ids?.length) {
      setValues(toOptions(values as Metadata[]));
    } else {
      searchReferences({
        nodeType,
        field,
        fieldKey,
        ids,
        maxResults,
        orderField,
        activeField
      }).then(r => {
        r?.forEach(m => {
          cache[m.props.refId] = m;
        });
        setValues(
          toOptions(
            toData(selected).filter(s => typeof s != 'string') as Metadata[]
          )
        );
      });
    }
  }, [selected]);
  React.useEffect(() => {
    handleSearch(text);
  }, [api]);
  React.useEffect(() => {
    if (minLength === 0 && !options.length) {
      handleSearch(null);
    }
  }, [minLength]);

  const getExcludeIds = () => {
    selected
      ?.map(s => {
        if (typeof s == 'string') return s;
        return s?.props?.refId;
      })
      .filter(x => x);
    return null;
  };

  const [query, setQuery] = React.useState('');

  const handleSearch = (q?: string) => {
    setQuery(q);
    setPage(0);
    const { searchReferences } = api || getAPI();
    if (q != text) {
      setText(q);
    }
    setIsLoading(true);
    searchReferences({
      nodeType,
      field,
      fieldKey,
      q,
      excludeIds: getExcludeIds(),
      includeBlanks,
      maxResults,
      orderField,
      seeOption,
      activeField
    }).then(result => {
      result?.forEach(m => {
        cache[m.props.refId] = m;
      });
      if (includeIds?.length) {
        setOptions(
          toOptions(result)
            .filter(o => includeIds.includes(o?.props?.refId))
            .filter(
              r =>
                !excludeRecords?.length ||
                !excludeRecords
                  .map(er => er.props.refId)
                  .includes(r?.props?.refId)
            )
        );
      } else {
        setOptions(
          toOptions(result)?.filter(
            r =>
              !excludeRecords?.length ||
              !excludeRecords
                .map(er => er.props.refId)
                .includes(r?.props?.refId)
          )
        );
      }
      setIsLoading(false);
    });
  };

  React.useEffect(() => {
    const valuesToAddBack = prevExcludeRecords?.filter(
      (pr: Metadata) =>
        !excludeRecords?.map(er => er.props.refId).includes(pr.props.refId)
    );
    if (options?.length) {
      setOptions([
        ...toOptions(valuesToAddBack),
        ...options?.filter(
          r =>
            !excludeRecords?.length ||
            !excludeRecords.map(er => er.props.refId).includes(r?.props?.refId)
        )
      ]);
      setPrevExcludeRecords(excludeRecords);
    }
  }, [excludeRecords]);

  React.useEffect(() => {
    if (seeOptions?.length) handleSearch(text);
  }, [seeOption, seeOptions]);

  const [endOfResults, setEndOfResults] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const handlePagination = () => {
    setPage(prevPage => prevPage + 1);
  };

  React.useEffect(() => {
    if (page > 0 && !endOfResults) {
      const { searchReferences } = api || getAPI();
      let q = query;
      let maxResults = 50 + page * 50;
      searchReferences({
        nodeType,
        field,
        fieldKey,
        q,
        excludeIds: getExcludeIds(),
        includeBlanks,
        maxResults: maxResults + 1,
        orderField,
        seeOption,
        activeField
      }).then(result => {
        result?.forEach(m => {
          cache[m.props.refId] = m;
        });
        if (result.length > maxResults) {
          setEndOfResults(false);
          result.pop();
        } else {
          setEndOfResults(true);
        }
        if (includeIds?.length) {
          setOptions(
            toOptions(result).filter(o => includeIds.includes(o?.props?.refId))
          );
        } else {
          setOptions(toOptions(result));
        }
        setIsLoading(false);
      });
    }
  }, [page, endOfResults]);
  const filterBy = (o: Option) =>
    values?.filter(v => v.props?.refId == o.props?.refId)[0] == null;
  const scrollContainer = React.useRef(null);
  const TMenu = React.useCallback(
    (results: any[]) => {
      return (
        <Menu className={cn({ 'with-see-option': seeOptions?.length > 1 })}>
          <SOpt
            selected={seeOption}
            options={seeOptions}
            setSelected={setSeeOpt}
          />
          <div className="ref-search-items" ref={scrollContainer}>
            {results.map((result, i) => {
              return (
                <MenuItem
                  option={result}
                  position={i}
                  key={result.props?.refId}
                >
                  <span
                    onClick={async e => {
                      const newValues = !multiple
                        ? [result]
                        : [
                            ...values?.filter(
                              v => v.props?.refId != result.props?.refId
                            ),
                            result
                          ];
                      setValues(newValues);
                      if (onChange) {
                        try {
                          const r = onChange(newValues as any) as any;
                          if (r?.then) await r;
                        } catch (err) {
                          setValues(values);
                        }
                      }
                    }}
                  >
                    <Pill name={result.rsLabel} data={result} />
                  </span>
                </MenuItem>
              );
            })}
            <InfiniteLoader
              container={scrollContainer}
              handleScroll={() => {
                handlePagination();
              }}
            ></InfiniteLoader>
          </div>
        </Menu>
      );
    },
    [page, multiple, onChange, seeOption, seeOptions, values]
  );
  return (
    <div
      className={cn(
        {
          'input-group': !hideIcon
        },
        'input-group hide'
      )}
    >
      <AsyncTypeahead
        id={`rs_${nodeType}`}
        ref={typeaheadRef}
        className={cn(
          {
            'rbt-rs-inline': inlineMenu,
            'fs-mask': shouldMask
          },
          'reference-search'
        )}
        filterBy={filterBy}
        isLoading={isLoading}
        labelKey="rsLabel"
        minLength={minLength === 0 ? 0 : minLength || 2}
        onSearch={() => {}}
        options={options}
        selected={values}
        useCache={false}
        maxResults={100000}
        paginate={false}
        onInputChange={q => {
          setEndOfResults(false);
          handleSearch(q);
        }}
        onMenuToggle={isOpen => {
          if (!isOpen) setSeeOpt(null);
        }}
        onChange={() => {}}
        renderToken={(option: Option) => (
          <Pill
            name={option.rsLabel}
            key={option.props.refId}
            data={option}
            className={getClassName(option)}
            isSelected={true}
          >
            {!props.disabled && (
              <Icon
                onClick={async () => {
                  const newValues = values.filter(
                    v => v.props?.refId != option.props?.refId
                  );
                  setValues(newValues);
                  if (onChange) {
                    try {
                      const r = onChange(newValues as any) as any;
                      if (r?.then) await r;
                    } catch (err) {
                      setValues(values);
                    }
                  }
                }}
              >
                <Close />
              </Icon>
            )}
          </Pill>
        )}
        renderMenu={TMenu}
        multiple
        {...props}
      />
      {!hideIcon && <SearchInput />}
    </div>
  );
};

const SOpt = ({
  selected,
  options,
  setSelected
}: {
  selected: SeeOption;
  options: SeeOption[];
  setSelected: (o: SeeOption) => void;
}) => {
  if (!options?.length || options.length < 2) return null;
  const s = selected || options[0];
  const idx = options.indexOf(s);
  const nextIdx = (idx + 1) % options.length;
  const next = options[nextIdx];
  return (
    <div className="see-more">
      <span onClick={() => setSelected(nextIdx == 0 ? null : next)}>
        See {next}
      </span>
    </div>
  );
};
